import { FC } from 'react';
import _ from 'lodash';

interface RadioInputProps {
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options: {
    value: string;
    label: string;
    description?: string;
  }[];
  values: any;
  handleChange: any;
  handleBlur: any;
  errors?: any;
  touched?: any;
  variant?: 'inline' | 'grid';
}

const RadioInput: FC<RadioInputProps> = ({ id, options, values, handleChange, label, errors, touched, required, variant = 'inline' }) => {
  return (
    <>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label} {required ? <span className="text-red-500">*</span> : ''}
      </label>
      <div className="mt-1 relative">
        <fieldset>
          <legend className="sr-only">{label}</legend>
          <div
            className={
              variant === 'grid'
                ? 'mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
                : 'mt-2 flex flex-wrap gap-4'
            }
          >
            {options.map(option => {
              const optionId = `${id}-${option.value}`;
              const optionDescriptionId = `${option.value}-description`;
              const isChecked = _.get(values, id) === option.value;

              if (variant === 'grid') {
                return (
                  <label
                    key={option.value}
                    htmlFor={optionId}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 shadow-sm transition ${
                      isChecked
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                        : 'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    <input
                      id={optionId}
                      aria-describedby={optionDescriptionId}
                      name={id}
                      type="radio"
                      onChange={handleChange}
                      value={option.value}
                      checked={isChecked}
                      className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">{option.label}</p>
                      {option.description && (
                        <p id={optionDescriptionId} className="text-gray-500">
                          {option.description}
                        </p>
                      )}
                    </div>
                  </label>
                );
              }

              return (
                <label key={option.value} htmlFor={optionId} className="flex items-start gap-3">
                  <input
                    id={optionId}
                    aria-describedby={optionDescriptionId}
                    name={id}
                    type="radio"
                    onChange={handleChange}
                    value={option.value}
                    checked={isChecked}
                    className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="text-sm space-x-3">
                    <span className="font-medium text-gray-700">{option.label}</span>
                    {option.description && (
                      <span id={optionDescriptionId} className="text-gray-500 font-light">
                        {option.description}
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </fieldset>
      </div>
      {_.get(errors, id) && _.get(touched, id) ? (
        <p className="mt-2 text-sm text-red-600" id={`${id}-error`}>
          {_.get(errors, id)}
        </p>
      ) : null}
    </>
  );
};

export default RadioInput;
